import React, { useEffect, useMemo, useState } from 'react';
import { Add as AddIcon } from '@mui/icons-material';

import ActionButton from '../../../components/ActionButton/ActionButton';
import { useSurveyRedCertStore } from '../../../../stores/surveyRedCertStore';
import {
  ActionCell,
  ActionLink,
  DeleteLink,
  EmptyTableMessage,
  ListWrapper,
  StyledTable,
  StyledTableContainer,
  TableCell,
  TableHeader,
  TableHeaderContainer,
  TableRow,
} from './styles';
import { SurveyListProps } from './types';
import { ConfirmActionModal } from '../ConfirmActionModal/ConfirmActionModal';
import { CustomTooltip } from '../../../components/Tooltip/Tooltip';
import { useI18n } from '../../../../hooks/useI18n';
import { TranslationKeys } from '../../../../i18n/locales/defaultResources';
import { useTranslationContext } from '../../../../context/TranslationContext';
import Loader from '../../../components/Loader/Loader';

const SurveyList: React.FC<SurveyListProps> = ({ onCreateNew, onEdit }) => {
  const { surveys, fetchSurveys, loading, error, deleteSurvey } =
    useSurveyRedCertStore();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [surveyToDelete, setSurveyToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const { t } = useI18n();
  const { isTranslationMode } = useTranslationContext();

  useEffect(() => {
    fetchSurveys();
  }, [fetchSurveys]);

  const handleDeleteClick = (surveyId: string, surveyName: string) => {
    setSurveyToDelete({ id: surveyId, name: surveyName });
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!surveyToDelete) return;

    try {
      await deleteSurvey(surveyToDelete.id);
      fetchSurveys();
    } catch (error) {
      console.error('Error deleting survey:', error);
    }
  };

  const handleEditSurvey = async (surveyId: string): Promise<void> => {
    onEdit(surveyId);
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatTime = (dateString: string | null): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const hasActiveSurvey = useMemo(() => {
    const now = new Date();
    return surveys.some(
      (survey) =>
        survey.data.status === 'active' ||
        (survey.data.startDate &&
          survey.data.endDate &&
          new Date(survey.data.startDate) <= now &&
          new Date(survey.data.endDate) >= now)
    );
  }, [surveys]);

  const createNewButtonContent = useMemo(() => {
    const buttonText = t(TranslationKeys.survey_list_create_new_survey);

    if (isTranslationMode && hasActiveSurvey) {
      return (
        <span
          data-translation-key={TranslationKeys.survey_list_create_new_survey}
        >
          {buttonText}
        </span>
      );
    }

    return (
      <ActionButton
        variant="link-button"
        buttonText={buttonText}
        onClickAction={onCreateNew}
        icon={<AddIcon />}
        disabled={hasActiveSurvey}
      />
    );
  }, [hasActiveSurvey, isTranslationMode, onCreateNew, t]);

  return (
    <ListWrapper>
      <TableHeaderContainer>
        {hasActiveSurvey ? (
          isTranslationMode ? (
            <div>
              <div
                style={{
                  background: '#97B053',
                  color: 'black',
                  padding: '8px',
                  borderRadius: '4px',
                  marginBottom: '8px',
                }}
              >
                <span
                  data-translation-key={
                    TranslationKeys.survey_list_create_new_survey_disabled_tooltip
                  }
                >
                  {t(
                    TranslationKeys.survey_list_create_new_survey_disabled_tooltip
                  )}
                </span>
              </div>
              <div
                style={{
                  background: '#97B053',
                  color: 'black',
                  padding: '8px',
                  borderRadius: '4px',
                  marginBottom: '50px',
                }}
              >
                <span
                  data-translation-key={
                    TranslationKeys.survey_list_create_new_survey
                  }
                >
                  {t(TranslationKeys.survey_list_create_new_survey)}
                </span>
              </div>
            </div>
          ) : (
            <CustomTooltip
              text={t(
                TranslationKeys.survey_list_create_new_survey_disabled_tooltip
              )}
              placement="top"
              showIcon={false}
            >
              <div>{createNewButtonContent}</div>
            </CustomTooltip>
          )
        ) : (
          createNewButtonContent
        )}
      </TableHeaderContainer>

      <StyledTableContainer>
        <StyledTable>
          <thead>
            <TableRow>
              <TableHeader>
                {t(TranslationKeys.survey_list_table_header_name)}
              </TableHeader>
              <TableHeader>
                {t(TranslationKeys.survey_list_table_header_type)}
              </TableHeader>
              <TableHeader>
                {t(TranslationKeys.survey_list_table_header_start_date)}
              </TableHeader>
              <TableHeader>
                {t(TranslationKeys.survey_list_table_header_start_time)}
              </TableHeader>
              <TableHeader>
                {t(TranslationKeys.survey_list_table_header_end_date)}
              </TableHeader>
              <TableHeader>
                {t(TranslationKeys.survey_list_table_header_end_time)}
              </TableHeader>
              <TableHeader>
                {t(TranslationKeys.survey_list_table_header_actions)}
              </TableHeader>
            </TableRow>
          </thead>
          <tbody>
            {loading ? (
              <TableRow>
                <EmptyTableMessage colSpan={7}>
                  <Loader size={40} />
                </EmptyTableMessage>
              </TableRow>
            ) : error ? (
              <TableRow>
                <EmptyTableMessage colSpan={7}>
                  {t(TranslationKeys.survey_list_error_loading)} {error.message}
                </EmptyTableMessage>
              </TableRow>
            ) : surveys.length === 0 ? (
              <TableRow>
                <EmptyTableMessage colSpan={7}>
                  {t(TranslationKeys.survey_list_no_surveys)}
                </EmptyTableMessage>
              </TableRow>
            ) : (
              surveys.map((survey) => (
                <TableRow key={survey.data.accessId}>
                  <TableCell>{survey.data.name}</TableCell>
                  <TableCell>{survey.data.type}</TableCell>
                  <TableCell>{formatDate(survey.data.startDate)}</TableCell>
                  <TableCell>{formatTime(survey.data.startDate)}</TableCell>
                  <TableCell>{formatDate(survey.data.endDate)}</TableCell>
                  <TableCell>{formatTime(survey.data.endDate)}</TableCell>
                  <TableCell>
                    <ActionCell>
                      <ActionLink
                        onClick={() => handleEditSurvey(survey.data.accessId)}
                      >
                        {t(TranslationKeys.survey_list_action_edit)}
                      </ActionLink>
                      <DeleteLink
                        onClick={() =>
                          handleDeleteClick(
                            survey.data.accessId,
                            survey.data.name
                          )
                        }
                      >
                        {t(TranslationKeys.survey_list_action_delete)}
                      </DeleteLink>
                    </ActionCell>
                  </TableCell>
                </TableRow>
              ))
            )}
          </tbody>
        </StyledTable>
      </StyledTableContainer>

      <ConfirmActionModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSurveyToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title={t(TranslationKeys.survey_list_delete_modal_title) as string}
        content={
          surveyToDelete
            ? `${t(TranslationKeys.survey_list_delete_modal_confirm_single)} ${surveyToDelete.name}? ${t(TranslationKeys.survey_list_delete_modal_warning)}`
            : `${t(TranslationKeys.survey_list_delete_modal_confirm_generic)} ${t(TranslationKeys.survey_list_delete_modal_warning)}`
        }
      />
    </ListWrapper>
  );
};

export default SurveyList;
